import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { gtplSaathiAPI } from '../services/api';
import toast from 'react-hot-toast';
import { RefreshCw, RotateCw, ShoppingCart, History, AlertTriangle, Inbox } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Notice, Stats, Stat, Item, Pill, Empty, Loading, Pager, StatusPill } from '../components/gtpl/GtplKit';

// Only 'renew' can plausibly need the "Place Order Instead" escape hatch — GTPL rejects
// Order for anyone who still has a package (see GtplSaathiSubscriberPage's Order-panel
// comment), so this only ever applies to a subscriber whose package fully dropped off GTPL's
// side, which surfaces as a renew failure, never as any other action type queued here.
const CAN_REORDER = new Set(['renew']);

const TASK_TYPE_LABEL = {
  customerTicket: 'GTPL Ticket', renew: 'Renew', multiRenew: 'Multi-Renew',
  multiRenewSubmit: 'Multi-Renew Submit', suspend: 'Suspend / Activate',
  orderPlace: 'Place Order', modify: 'Modify Profile', hardwareReplacement: 'Hardware Replacement',
};
const STATUS_TONE = { queued: 'warn', succeeded: 'ok', failed: 'bad' };
const STATUS_LABEL = { queued: 'Queued', succeeded: 'Succeeded', failed: 'Failed' };

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

export default function GtplRetryQueuePage() {
  useDarkReady();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState({});
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [status, setStatus] = useState('');
  const [actionType, setActionType] = useState('');
  const [page, setPage] = useState(1);
  const [retryingId, setRetryingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await gtplSaathiAPI.getPendingTasks({ page, limit: 25, status: status || undefined, actionType: actionType || undefined });
      setTasks(data.data || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
      setStatusCounts(data.statusCounts || {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load GTPL retry queue');
    } finally {
      setLoading(false);
    }
  }, [page, status, actionType]);

  useEffect(() => { load(); }, [load]);

  // Any filter change resets back to page 1 — a stale page number past the new filtered
  // total would otherwise just render an empty table with no clue why.
  function changeStatus(next) { setStatus(next); setPage(1); }
  function changeActionType(next) { setActionType(next); setPage(1); }

  const handleRetry = async (task) => {
    setRetryingId(task._id);
    try {
      const { data } = await gtplSaathiAPI.retryPendingTask(task._id);
      toast.success(data.data?.resultMessage || 'Retried successfully');
      load();
    } catch (err) {
      // The task itself still got updated (queued-again or failed) even though this call
      // errored — the error message is that new outcome, not a "nothing happened" failure.
      toast.error(err.response?.data?.message || 'Retry failed');
      load();
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Screen>
      <PageHead icon={History} tone="warn" title="Retry queue" sub="GTPL actions that could not go through, kept here and tried again.">
        <button type="button" className="btn btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={15} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /> Refresh
        </button>
      </PageHead>

      <Notice tone="info">
        When the GTPL login has expired, every GTPL write (ticket, renew, multi-renew, suspend, order, modify,
        box replacement) waits here instead of failing. Pasting a fresh login in <b>Settings → GTPL Saathi</b> replays
        everything queued, oldest first.
      </Notice>

      <Stats>
        <Stat tone="warn" value={statusCounts.queued ?? 0} label="Queued" active={status === 'queued'} onClick={() => changeStatus(status === 'queued' ? '' : 'queued')} />
        <Stat tone="bad" value={statusCounts.failed ?? 0} label="Failed" active={status === 'failed'} onClick={() => changeStatus(status === 'failed' ? '' : 'failed')} />
        <Stat tone="ok" value={statusCounts.succeeded ?? 0} label="Succeeded" active={status === 'succeeded'} onClick={() => changeStatus(status === 'succeeded' ? '' : 'succeeded')} />
      </Stats>

      <div className="gx-actions" style={{ marginBottom: 12 }}>
        <select className="form-control" style={{ maxWidth: 260 }} value={actionType} onChange={e => changeActionType(e.target.value)} aria-label="Task type">
          <option value="">All task types</option>
          {Object.entries(TASK_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        {(status || actionType) && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { changeStatus(''); changeActionType(''); }}>Clear filters</button>
        )}
      </div>

      {loading ? <Loading /> : tasks.length === 0 ? (
        <Empty icon={Inbox} tone="ok" title="Nothing here">No actions match this filter.</Empty>
      ) : (
        <div className="gx-list">
          {tasks.map((t) => {
            const retrying = retryingId === t._id;
            return (
              <Item key={t._id} tone={STATUS_TONE[t.status] || 'mute'}
                title={TASK_TYPE_LABEL[t.actionType] || t.actionType}
                sub={t.label}
                right={<StatusPill status={t.status === 'succeeded' ? 'done' : t.status}>{STATUS_LABEL[t.status] || t.status}</StatusPill>}
                meta={<>
                  <span>Customer <b>{t.customer ? `${t.customer.name} (${t.customer.customerId})` : '—'}</b></span>
                  <span>{fmtDateTime(t.createdAt)} · by {t.raisedByName || '—'}</span>
                  {t.attempts > 1 && <span>{t.attempts} attempts</span>}
                </>}
                actions={<>
                  {(t.status === 'queued' ? t.queuedReason : (t.resultMessage || t.queuedReason)) && (
                    <div className="gx-hint" style={{ flexBasis: '100%', margin: 0 }}>
                      {t.status === 'queued' ? t.queuedReason : (t.resultMessage || t.queuedReason)}
                    </div>
                  )}
                  {t.needsVerification && (
                    <div style={{ flexBasis: '100%' }}>
                      <Pill tone="warn"><AlertTriangle size={12} /> Check gtplsaathi.com first: this may already have gone through</Pill>
                    </div>
                  )}
                  <button type="button" className="btn btn-sm btn-primary" disabled={retrying} onClick={() => handleRetry(t)}>
                    <RotateCw size={13} style={retrying ? { animation: 'spin 1s linear infinite' } : undefined} />
                    {retrying ? 'Retrying…' : `Retry${t.actionType === 'renew' ? ' as Renew' : ''}`}
                  </button>
                  {CAN_REORDER.has(t.actionType) && (t.payload?.stb || t.payload?.accountNumber) && (
                    <Link
                      to={`/gtpl-saathi-subscriber?${new URLSearchParams({
                        ...(t.payload.stb ? { stb: t.payload.stb } : {}),
                        ...(t.payload.accountNumber ? { accountNumber: t.payload.accountNumber } : {}),
                        focus: 'order',
                      })}`}
                      className="btn btn-sm btn-outline"
                    >
                      <ShoppingCart size={13} /> Place Order Instead
                    </Link>
                  )}
                </>}
              />
            );
          })}
        </div>
      )}

      <Pager page={pagination.page} pages={pagination.pages} total={pagination.total} onPage={setPage} />
    </Screen>
  );
}
